<script lang="ts" setup>
  import { computed, ref, useSlots } from 'vue';

  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import Icon from '../Icon/Icon.vue';
  import Input from '../Input/Input.vue';

  export interface SearchBarProps {
    /**
     * Indicates loading activity (for example async request) that should prevent search
     */
    isLoading?: boolean;

    /**
     * Sets a visual indicator that the search is active
     */
    isWorking?: boolean;

    /**
     * The search term
     */
    modelValue?: string;

    /**
     * Placeholder text for the input
     */
    placeholder?: string;

    /**
     * Label for search bar
     */
    label?: string;

    /**
     * Text below the input
     */
    hintText?: string;
  }

  defineOptions({
    name: 'll-search-bar',
  });

  const props = withDefaults(defineProps<SearchBarProps>(), {
    isLoading: false,
    isWorking: false,
    modelValue: '',
    placeholder: t('ll.search'),
    label: t('ll.search'),
    hintText: undefined,
  });

  const emit = defineEmits<{
    /**
     * Emitted when searching or clearing the search input
     */
    (e: 'search', searchTerm: string): void;

    /**
     * Emitted when the search term changes
     */
    (e: 'update:model-value', searchTerm: string): void;
  }>();

  const slots = useSlots();
  const searchTerm = ref(props.modelValue);
  const stashInputRef = ref<InstanceType<typeof Input>>();

  const searchTabIndex = computed(() => (searchTerm.value ? 0 : -1));

  function onSearch() {
    if (props.isLoading || props.isWorking) {
      return;
    }
    emit('search', searchTerm.value);
  }

  function clearSearchTerm() {
    searchTerm.value = '';
    emit('search', '');
    emit('update:model-value', '');

    stashInputRef.value?.inputEl?.focus();
  }

  function handleChange() {
    emit('update:model-value', searchTerm.value);
  }
</script>

<template>
  <Input
    ref="stashInputRef"
    v-model="searchTerm"
    class="stash-search-bar"
    :class="[{ 'stash-search-bar--value': !!searchTerm }]"
    :label="props.label"
    :placeholder="props.placeholder"
    :hint-text="props.hintText"
    data-test="stash-search-bar"
    @update:model-value="handleChange"
    @keyup.enter="onSearch"
  >
    <template #append>
      <Button
        v-if="searchTerm"
        class="stash-search-bar__clear-button"
        :disabled="props.isLoading"
        data-test="stash-search-bar|clear-button"
        @click="clearSearchTerm"
        @keyup.enter="clearSearchTerm"
        @keyup.space="clearSearchTerm"
      >
        <Icon name="close" />
      </Button>
      <Button
        :tabindex="searchTabIndex"
        data-test="stash-search-bar|search-button"
        :disabled="props.isLoading"
        class="stash-search-bar__search-button"
        @click="onSearch"
        @keyup.enter="onSearch"
        @keyup.space="onSearch"
      >
        <Icon :class="{ 'animate-spin': props.isWorking }" :name="props.isWorking ? 'loading-empty' : 'search'" />
      </Button>
    </template>
    <template v-if="slots.hint" #hint>
      <!-- @slot Overrides the Input hint-text prop -->
      <slot name="hint"></slot>
    </template>
  </Input>
</template>

<style module>
  @reference "../../../styles/main.css";

  :global(:where(.stash-search-bar) input) {
    text-overflow: ellipsis;
  }

  :global(:where(.stash-search-bar).stash-search-bar--value input) {
    padding-right: 72px;
  }

  :global(:where(.stash-search-bar) input:hover) {
    border: 1px solid var(--color-blue-500);
  }

  :global(:where(.stash-search-bar) .stash-input-append) {
    display: flex;
    justify-content: flex-end;
    width: 72px; /* double the the default width of 36px */
    overflow: visible;
  }

  :global(:where(.stash-search-bar) .stash-search-bar__clear-button) {
    color: var(--color-ice-700);
    height: --spacing(9);
    min-width: --spacing(9);
    width: --spacing(9);
    background: none;
    display: flex;
    align-items: center;
    justify-content: center;

    &:focus-visible {
      outline: none;
      box-shadow: none;

      & svg {
        border-radius: var(--radius-sm);

        @apply ring-4 ring-blue-500/15;
      }
    }
  }

  :global(:where(.stash-search-bar) .stash-search-bar__clear-button:disabled) {
    color: var(--color-ice-500);
    cursor: default;
  }

  :global(:where(.stash-search-bar) .stash-search-bar__search-button) {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    height: --spacing(9);
    min-width: --spacing(9);
    width: --spacing(9);
    display: flex;
    align-items: center;
    justify-content: center;

    &:focus {
      box-shadow: none;
    }

    &:disabled {
      border-color: var(--color-ice-500);
    }

    &:hover {
      background-color: var(--color-blue-600);
    }

    &:active {
      background-color: var(--color-blue-700);
    }

    &:focus-visible {
      outline: none;
      border: 1px solid var(--color-blue-900);
      /* Simulating a bigger border so the design does not break */
      box-shadow:
        inset 0 0 0 1px var(--color-blue-900),
        0 0 0 4px rgb(0 114 240 / 15%); /* Unfortunatelly, we have to use RGBA to make the shadow transparent, this is blue-500 */
    }
  }
</style>
